import { getColorByRoofZone } from '__editor/panelsEditor/models/panel';
import { DEBUG } from 'debug';
import { ON_THE_CORNER_COLOR } from './panelColor';
import { drawHorizontalLine } from '../scaleCursorModel';

// UBuilder-Lite-only panel/bay texture pipeline. Routed to from panelTexture.ts
// when state().app.isUbuilderLite is true. Adds rounded corners, an inner cell
// grid, an inner highlight border, texture caching for bay/pending-removal
// sprites, and explicit Graphics.destroy() to keep GPU memory bounded across
// the longer-lived Lite design sessions. Legacy editor keeps the original
// non-cached drawRect implementation untouched.

const pendingRemovalTextures: { [textureKey: string]: PIXI.Texture } = {};
const bayTextures: { [textureKey: string]: PIXI.Texture } = {};

export function exposablePanelTextureLite(
  width: number,
  height: number,
  roofZone: roofZoneNumber,
  nearObstruction: boolean,
  exposed: boolean,
  exposedNeighbour: boolean,
  id: number | string,
  panelConfig: number = undefined,
  skewAffected?: number,
) {
  const borderSize = 1;
  let borderColor: number;
  if (DEBUG.highlightExposedAndExposedNeighbour) {
    if (exposed === true && exposedNeighbour === true) {
      borderColor = 0xeb4034;
    } else if (exposed === true && exposedNeighbour === false) {
      borderColor = 0x37eb34;
    } else if (exposed === false && exposedNeighbour === true) {
      borderColor = 0x16b6fa;
    } else if (exposed === false && exposedNeighbour === false) {
      borderColor = 0x000000;
    }
  } else {
    borderColor = 0x000000;
  }
  const COLOR_DARK_GREY = 0xA9A9A9;
  const COLOR_BLACK = 0x000000;

  return _baseLitePanelTexture({
    width,
    height,
    backgroundColor: getColorByRoofZone(roofZone, nearObstruction, skewAffected),
    border: (exposed || exposedNeighbour ?
      {
        color: COLOR_DARK_GREY,
        width: borderSize,
      } : {
        color: COLOR_BLACK,
        width: borderSize,
      }
    ),
    text: id ? String(id) : null,
    panelConfig,
  });
}

export function panelTextureLite(
  width: number,
  height: number,
  roofZone: roofZoneNumber,
  nearObstruction: boolean,
  id: number | string,
  attached: boolean = false,
  isRMGridflex: boolean = false,
  panelConfig: number = undefined,
  skewAffected?: number,
) {
  const borderSize = 2;
  const COLOR_BLACK = 0x000000;

  return _baseLitePanelTexture({
    width,
    height,
    backgroundColor: (isRMGridflex && !!attached ? ON_THE_CORNER_COLOR : getColorByRoofZone(roofZone, nearObstruction, skewAffected)),
    border: {
      color: COLOR_BLACK,
      width: borderSize,
    },
    text: id ? String(id) : null,
    panelConfig,
  });
}

export function pendingRemovalPanelTextureLite(
  width: number,
  height: number,
  panelConfig: number = undefined,
) {
  const textureKey = `${width}-${height}-${panelConfig ?? ''}`;

  if (pendingRemovalTextures[textureKey]) {
    return pendingRemovalTextures[textureKey];
  }

  pendingRemovalTextures[textureKey] = _baseLitePanelTexture({
    width,
    height,
    backgroundColor: 0xDC2626,
    border: {
      color: 0x7F1D1D,
      width: 2,
    },
    panelConfig,
  });

  return pendingRemovalTextures[textureKey];
}

interface PanelTextureConfig {
  width: number;
  height: number;
  backgroundColor: number;
  text?: string;
  border: {
    color: number;
    width: number;
  };
  panelConfig: number;
}

function _baseLitePanelTexture(config: PanelTextureConfig): PIXI.Texture {
  const { backgroundColor, width, height, border: { width: borderWidth, color: borderColor }, panelConfig } = config;
  const panelTexture = new PIXI.Graphics(true);

  const W = Math.ceil(width * 2);
  const H = Math.ceil(height * 2);
  const cornerRadius = 4;

  panelTexture.beginFill(backgroundColor);
  panelTexture.lineStyle(borderWidth, borderColor);
  panelTexture.drawRoundedRect(0, 0, W, H, cornerRadius);
  panelTexture.endFill();

  const cols = W >= H ? 10 : 6;
  const rows = W >= H ? 6 : 10;
  const cellW = W / cols;
  const cellH = H / rows;
  panelTexture.lineStyle(0.6, 0xFFFFFF, 0.18);
  for (let ci = 1; ci < cols; ci++) {
    panelTexture.moveTo(ci * cellW, 2);
    panelTexture.lineTo(ci * cellW, H - 2);
  }
  for (let ri = 1; ri < rows; ri++) {
    panelTexture.moveTo(2, ri * cellH);
    panelTexture.lineTo(W - 2, ri * cellH);
  }

  panelTexture.lineStyle(1, 0x1A4F7A, 0.6);
  panelTexture.drawRoundedRect(1, 1, W - 2, H - 2, Math.max(0, cornerRadius - 0.5));

  panelTexture.beginFill(backgroundColor, 0);

  if (panelConfig == 1 || panelConfig == 3) {
    panelTexture.addChild(drawHorizontalLine(Math.ceil(width / 3), Math.ceil(width / 3), 6));
    panelTexture.addChild(drawHorizontalLine(Math.ceil(2 * width / 3), Math.ceil(2 * width / 3), 6));
  }

  if (config.text) {
    panelTexture.addChild(_panelIdText(0, 0, width, height, config.text));
  }

  panelTexture.endFill();
  // 2× minimum (or devicePixelRatio if higher) so panel edges stay sharp
  // when zoomed. Matches the pattern in leadEdge.ts.
  const texture = panelTexture.generateCanvasTexture(PIXI.SCALE_MODES.LINEAR, Math.max(2, window.devicePixelRatio || 1));
  panelTexture.destroy({ children: true });
  return texture;
}

function _panelIdText(x: number, y: number, width: number, height: number, text?: string) {
  const tooltip: PIXI.Text = new PIXI.Text(text, {
    fontSize: '42px',
  });

  tooltip.x = x;
  tooltip.y = y;
  tooltip.width = width * 2;
  tooltip.height = height * 2;
  tooltip.id = 'panelIdText';
  return tooltip;
}

export function _baseBayTextureLite(width, height, attached: 0 | 1 = 0): PIXI.Texture {
  const textureKey = `${width}-${height}-${attached}`;

  if (bayTextures[textureKey]) {
    return bayTextures[textureKey];
  }

  const panelTexture = new PIXI.Graphics(true);
  const bayColor = attached ? 0xf2682A : 0x2A81BF;
  panelTexture.beginFill(bayColor);
  panelTexture.lineStyle(1, 0x000000);
  panelTexture.drawRect(0, 0, Math.ceil(width * 2), Math.ceil(height * 2));
  panelTexture.endFill();
  // 2× minimum (or devicePixelRatio if higher) so panel edges stay sharp
  // when zoomed. Matches the pattern in leadEdge.ts.
  bayTextures[textureKey] = panelTexture.generateCanvasTexture(PIXI.SCALE_MODES.LINEAR, Math.max(2, window.devicePixelRatio || 1));
  panelTexture.destroy({ children: true });
  return bayTextures[textureKey];
}

export function clearGeneratedPanelTexturesLite() {
  Object.keys(pendingRemovalTextures).forEach((key) => {
    pendingRemovalTextures[key].destroy(true);
    delete pendingRemovalTextures[key];
  });

  Object.keys(bayTextures).forEach((key) => {
    bayTextures[key].destroy(true);
    delete bayTextures[key];
  });
}
